import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  format,
  getDaysInMonth,
  isSameDay,
  isWeekend,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { es } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getApiErrorMessage, getMaterialReservas } from "@/lib/api";
import { MATERIAL_ESTADOS, formatReservaRango, isReservaRetrasada } from "@/lib/material";
import { MaterialFoto, MaterialUser, ReservaEstadoBadge } from "@/components/material/MaterialShared";
import { UserAvatar } from "@/components/UserAvatar";

const VISTAS = [
  { id: "week", label: "Semana", colMin: 112 },
  { id: "2weeks", label: "2 semanas", colMin: 64 },
  { id: "month", label: "Mes", colMin: 40 },
];
const LEFT_COL = 208;

function rangoVista(vista, anchor) {
  if (vista === "month") {
    const start = startOfMonth(anchor);
    return { start, days: getDaysInMonth(anchor) };
  }
  return { start: startOfWeek(anchor, { weekStartsOn: 1 }), days: vista === "2weeks" ? 14 : 7 };
}

function textoSobre(hex) {
  const h = (hex || "#3b82f6").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#111827" : "#ffffff";
}

function estiloBarra(reserva, color, retrasada) {
  if (reserva.estado === "devuelta") {
    return { className: "bg-zinc-200/80 text-zinc-600 border border-zinc-300", style: {} };
  }
  if (retrasada) {
    return { className: "bg-red-500 text-white border border-red-600 shadow-sm shadow-red-500/30", style: {} };
  }
  if (reserva.estado === "en_uso") {
    return { className: "shadow-sm", style: { background: color, color: textoSobre(color), border: `1px solid ${color}` } };
  }
  return {
    className: "text-foreground",
    style: {
      background: `repeating-linear-gradient(135deg, ${color}26 0 6px, ${color}4d 6px 12px)`,
      border: `1.5px dashed ${color}`,
    },
  };
}

export default function MaterialTimeline({ items, usersByEmail, me, refreshKey, onSelectRange, onOpenItem }) {
  const [vista, setVista] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "week" : "2weeks"));
  const [anchor, setAnchor] = useState(() => new Date());
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(null); // { itemId, a, b } índices de día
  const [now, setNow] = useState(() => new Date());

  const { start, days } = useMemo(() => rangoVista(vista, anchor), [vista, anchor]);
  const dias = useMemo(() => Array.from({ length: days }, (_, i) => addDays(start, i)), [start, days]);
  const end = useMemo(() => addDays(start, days), [start, days]);
  const rs = start.getTime();
  const span = end.getTime() - rs;
  const hoy = startOfDay(now);
  const colMin = VISTAS.find((v) => v.id === vista)?.colMin ?? 64;

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    getMaterialReservas({ desde: start.toISOString(), hasta: end.toISOString() })
      .then((d) => !cancel && setReservas(d.reservas || []))
      .catch((e) => !cancel && toast.error(getApiErrorMessage(e, "No se pudo cargar el calendario")))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [start, end, refreshKey]);

  const porItem = useMemo(() => {
    const m = {};
    reservas.forEach((r) => {
      (m[r.item_id] = m[r.item_id] || []).push(r);
    });
    return m;
  }, [reservas]);

  const terminarDrag = useCallback(() => {
    setDrag((d) => {
      if (d) {
        const a = Math.min(d.a, d.b);
        const b = Math.max(d.a, d.b);
        setTimeout(() => onSelectRange(d.itemId, dias[a], dias[b]), 0);
      }
      return null;
    });
  }, [dias, onSelectRange]);

  useEffect(() => {
    if (!drag) return undefined;
    window.addEventListener("mouseup", terminarDrag);
    return () => window.removeEventListener("mouseup", terminarDrag);
  }, [drag, terminarDrag]);

  const mover = (dir) => {
    if (vista === "month") setAnchor((a) => addMonths(a, dir));
    else setAnchor((a) => addWeeks(a, dir));
  };

  const pct = (ms) => ((ms - rs) / span) * 100;
  const nowPct = now >= start && now < end ? pct(now.getTime()) : null;
  const titulo =
    vista === "month"
      ? format(start, "MMMM yyyy", { locale: es })
      : `${format(start, "d MMM", { locale: es })} – ${format(addDays(end, -1), "d MMM yyyy", { locale: es })}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8 bg-background" onClick={() => mover(-1)} aria-label="Anterior">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8 bg-background" onClick={() => setAnchor(new Date())}>
            Hoy
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8 bg-background" onClick={() => mover(1)} aria-label="Siguiente">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <h2 className="text-base font-semibold capitalize">{titulo}</h2>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <div className="ml-auto flex rounded-lg border bg-muted p-0.5 shadow-sm">
          {VISTAS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVista(v.id)}
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                vista === v.id ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card shadow-sm">
        <div style={{ minWidth: LEFT_COL + days * colMin }} className="select-none">
          {/* Cabecera de días */}
          <div className="flex border-b bg-muted/30">
            <div style={{ width: LEFT_COL }} className="sticky left-0 z-20 shrink-0 border-r bg-muted/30 backdrop-blur px-3 py-2 text-xs font-medium text-muted-foreground">
              Equipo
            </div>
            <div className="flex flex-1">
              {dias.map((d) => {
                const esHoy = isSameDay(d, now);
                return (
                  <div
                    key={d.toISOString()}
                    className={cn(
                      "flex-1 border-r last:border-r-0 py-1.5 text-center",
                      isWeekend(d) && "bg-muted/50",
                      esHoy && "bg-primary/10"
                    )}
                  >
                    <div className={cn("text-[10px] uppercase tracking-wide text-muted-foreground", esHoy && "text-primary font-semibold")}>
                      {format(d, vista === "month" ? "EEEEE" : "EEE", { locale: es })}
                    </div>
                    <div
                      className={cn(
                        "mx-auto mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-sm",
                        esHoy ? "bg-primary text-primary-foreground font-semibold" : "text-foreground"
                      )}
                    >
                      {format(d, "d")}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {items.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-muted-foreground">No hay equipos que mostrar.</div>
          )}

          {items.map((item) => {
            const cfg = MATERIAL_ESTADOS[item.estado] || MATERIAL_ESTADOS.disponible;
            const filas = [...(porItem[item.id] || [])];
            // Un retraso fuera del rango sigue ocupando el equipo hasta hoy
            if (item.actual && item.estado === "retrasado" && !filas.some((r) => r.id === item.actual.id)) {
              filas.push(item.actual);
            }
            return (
              <div key={item.id} className="flex border-b last:border-b-0 group/row">
                <button
                  type="button"
                  onClick={() => onOpenItem(item)}
                  style={{ width: LEFT_COL }}
                  className="sticky left-0 z-20 flex shrink-0 items-center gap-2.5 border-r bg-card px-3 py-2 text-left hover:bg-muted/60"
                >
                  <MaterialFoto item={item} className="h-9 w-9 shrink-0 rounded-md" iconClassName="w-4 h-4" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{item.nombre}</span>
                    <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <span className={cn("h-1.5 w-1.5 rounded-full", cfg.dot)} />
                      {cfg.label}
                    </span>
                  </span>
                </button>
                <div className="relative flex flex-1" style={{ minHeight: 56 }}>
                  {dias.map((d, i) => {
                    const pasado = d < hoy;
                    const enDrag = drag && drag.itemId === item.id && i >= Math.min(drag.a, drag.b) && i <= Math.max(drag.a, drag.b);
                    return (
                      <div
                        key={i}
                        onMouseDown={(e) => {
                          if (pasado || e.button !== 0 || item.estado === "reparacion") return;
                          e.preventDefault();
                          setDrag({ itemId: item.id, a: i, b: i });
                        }}
                        onMouseEnter={() => {
                          if (drag && drag.itemId === item.id && !pasado) setDrag((dr) => ({ ...dr, b: i }));
                        }}
                        className={cn(
                          "flex-1 border-r last:border-r-0 transition-colors",
                          isWeekend(d) && "bg-muted/30",
                          pasado ? "bg-[repeating-linear-gradient(45deg,transparent_0_6px,hsl(var(--muted))_6px_7px)]" : item.estado !== "reparacion" && "cursor-cell hover:bg-primary/5",
                          enDrag && "!bg-primary/20"
                        )}
                      />
                    );
                  })}

                  {item.estado === "reparacion" && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-zinc-100/70 text-xs font-medium text-zinc-500">
                      En reparación
                    </div>
                  )}

                  {filas.map((r) => {
                    const retrasada = isReservaRetrasada(r, now);
                    const ini = new Date(r.inicio).getTime();
                    const fin = retrasada ? Math.max(new Date(r.fin).getTime(), now.getTime()) : new Date(r.fin).getTime();
                    if (fin <= rs || ini >= rs + span) return null;
                    const left = Math.max(0, pct(ini));
                    const right = Math.min(100, pct(fin));
                    const u = usersByEmail[r.user_email] || { email: r.user_email, nombre: r.user_email };
                    const color = u.color || "#3b82f6";
                    const { className, style } = estiloBarra(r, color, retrasada);
                    const ancho = right - left;
                    return (
                      <Tooltip key={r.id}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => onOpenItem(item, r)}
                            className={cn(
                              "absolute top-2 bottom-2 z-10 flex items-center gap-1.5 overflow-hidden rounded-md px-1.5 text-left text-xs font-medium transition-transform hover:-translate-y-px hover:shadow-md",
                              pct(ini) < 0 && "rounded-l-none",
                              pct(fin) > 100 && "rounded-r-none",
                              className
                            )}
                            style={{ left: `${left}%`, width: `calc(${ancho}% - 2px)`, ...style }}
                          >
                            <UserAvatar user={u} size={18} className="shrink-0" />
                            <span className="truncate">
                              {r.user_email === me ? "Tú" : u.nombre}
                              {r.motivo ? <span className="opacity-75 font-normal"> · {r.motivo}</span> : null}
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div className="flex flex-col gap-1 py-0.5">
                            <div className="flex items-center justify-between gap-3">
                              <MaterialUser email={r.user_email} usersByEmail={usersByEmail} me={me} size={16} nameClassName="font-medium" />
                              <ReservaEstadoBadge reserva={r} retrasada={retrasada} />
                            </div>
                            <span className="text-muted-foreground">{formatReservaRango(r)}</span>
                            {r.motivo && <span>{r.motivo}</span>}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}

                  {nowPct !== null && (
                    <div className="pointer-events-none absolute inset-y-0 z-[15] w-0.5 bg-red-500/80" style={{ left: `${nowPct}%` }}>
                      <span className="absolute -left-[3px] top-0 h-2 w-2 rounded-full bg-red-500" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-6 rounded-sm border-[1.5px] border-dashed border-sky-500 bg-[repeating-linear-gradient(135deg,#0ea5e926_0_4px,#0ea5e94d_4px_8px)]" />
          Reservado
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-6 rounded-sm bg-sky-500" />
          En uso (color de la persona)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-6 rounded-sm bg-red-500" />
          Retrasado
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-6 rounded-sm border border-zinc-300 bg-zinc-200" />
          Devuelto
        </span>
        <span className="ml-auto hidden sm:inline">Haz clic o arrastra sobre los días libres para reservar</span>
      </div>
    </div>
  );
}
